import { Dispatch, useCallback, useEffect, useState } from 'react';
import LocationApiService, { PlaceidApiProps, Place } from '../../services/location-api/location-api-service';
import { stripCountry, useNormalizePostalCode, useStateAbbreviations } from '../use-normalized-address/useNormalizedAddress';
import { CurrentLocation } from '../../models/app-data-model';
import { copySync } from 'fs-extra';

export type PlaceidApiServiceStatus = {
  response: Array<Place>;
  isRunning: boolean;
  hasError: boolean;
  error?: any;
};

/**
 * This PlaceID Api Hook is used to fetch data from the AWS Location service at RUNTIME
 * (instead of at buildtime like our graphql)
 * @param queries
 * @returns an array of locations
 */

export const PlaceCategory = {
  PostalCodeType: "postalcodetype",
  MunicipalityType: "municipalitytype",
  AddressType: "addresstype"
};

const usePlaceApi = (placeidProps: PlaceidApiProps): [PlaceidApiServiceStatus, Dispatch<React.SetStateAction<PlaceidApiProps>>] => {
  const [placeQuery, setPlaceQuery] = useState(placeidProps);
  const [isRunning, setIsRunning] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState(undefined as any);
  const [response, setResponse] = useState(new Array<Place>());
  const [apiStatus, setApiStatus] = useState({ isRunning: false, response: response } as PlaceidApiServiceStatus);

  useEffect(() => {
    setApiStatus({
      isRunning: isRunning,
      hasError: hasError,
      response: response,
      error: error
    });

  }, [isRunning, hasError, response, error]);

  const apiCall = useCallback(async () => {
    setIsRunning(true);
    setHasError(false);

    if (placeidProps?.placeId) {
      try {
        const response = await LocationApiService.getPlaceResults(placeidProps) as Place[];

        setIsRunning(false);
        setResponse(response);
      } catch (err) {
        setError(err);
        setHasError(true);
        setIsRunning(false);
      }
    }

  }, [placeidProps.placeId]);

  useEffect(() => {
    apiCall();
  }, [placeidProps.placeId]);

  return [apiStatus, setPlaceQuery];
};

const getCurrentLocationFromPlace = (place: any) => {
  if (!place) {
    return;
  }

  let municipality = place.Municipality || place.Region || place.SubRegion;
  const region = useStateAbbreviations(place?.Region);
  const postalCode = useNormalizePostalCode(place?.PostalCode);
  const origin = `${place.Geometry.Point[1]},${place.Geometry.Point[0]}`;
  let label = stripCountry(place.Label);

  switch (place.Categories?.[0].toLowerCase()) {
    case PlaceCategory.PostalCodeType:
      if (place.Country && place.Country?.toLowerCase() == "usa") {
        const labelParts = place.Label.split(",");
        municipality = labelParts[1].trim();
      }
      break;
    case PlaceCategory.AddressType:
      const firstComma = label.indexOf(",");
      if (firstComma === -1) return label;
      label = label.slice(firstComma + 1, label.length).trim();
      break;
  }

  const currentLocaton: CurrentLocation = {
    municipality: municipality,
    region: region,
    subRegion: place.SubRegion || "",
    postalCode: postalCode,
    country: place.Country || "",
    label: label,
    origin: origin
  };
  return currentLocaton;

}
export { usePlaceApi, getCurrentLocationFromPlace };
